import React, { useState, useMemo } from "react";
import { useDispatch } from "react-redux";
import {
  Button,
  Divider,
  Header,
  Segment,
  Form,
  Dropdown,
} from "semantic-ui-react";
import { closeModal } from "../../../app/common/modals/modalSlice";
import ModalWrapper from "../../../app/common/modals/modalWrapper";
import { updateDocInDb } from "../../../app/firestore/firestoreService";
import { toast } from "react-toastify";

export default function ComplianceAssignDocumentTypeModal({ document, template }) {
  const dispatch = useDispatch();
  const [documentName, setDocumentName] = useState(document.documentName || "");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Extract unique documentName options from template, ordered by category and item order
  // Display format: "Category Name - Item Name"
  const documentNameOptions = useMemo(() => {
    if (!template?.categories) return [];
    const nameOrderMap = new Map();
    template.categories.forEach((category) => {
      (category.items || []).forEach((item) => {
        if (item.documentName && !nameOrderMap.has(item.documentName)) {
          nameOrderMap.set(item.documentName, {
            categoryName: category.name || "",
            categoryOrder: category.order || 0,
            itemOrder: item.order || 0,
          });
        }
      });
    });
    return Array.from(nameOrderMap.entries())
      .sort((a, b) => {
        if (a[1].categoryOrder !== b[1].categoryOrder) {
          return a[1].categoryOrder - b[1].categoryOrder;
        }
        return a[1].itemOrder - b[1].itemOrder;
      })
      .map(([name, info]) => ({
        key: name,
        text: `${info.categoryName} - ${name}`,
        value: name,
      }));
  }, [template]);

  async function handleSubmit() {
    setIsSubmitting(true);
    try {
      await updateDocInDb(document.id, {
        documentName: documentName || null,
      });
      toast.success("Document name assigned successfully");
      dispatch(closeModal({ modalType: "ComplianceAssignDocumentTypeModal" }));
    } catch (error) {
      console.error("Error assigning document name:", error);
      toast.error("Failed to assign document name");
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleClose() {
    dispatch(closeModal({ modalType: "ComplianceAssignDocumentTypeModal" }));
  }

  return (
    <ModalWrapper size="small">
      <Segment clearing>
        <Header size="large" color="blue">
          Assign / Change Document Name
        </Header>
        <p className="text-grey">
          Assign or change the compliance document name for: <strong>{document.name}</strong>
        </p>
        <Divider />
        <Form>
          <Form.Field>
            <label>Document Name</label>
            <Dropdown
              placeholder="Select Document Name"
              fluid
              selection
              clearable
              search
              options={documentNameOptions}
              value={documentName}
              onChange={(e, { value }) => setDocumentName(value)}
            />
          </Form.Field>
        </Form>
        <Divider />
        <Button
          primary
          floated="right"
          onClick={handleSubmit}
          loading={isSubmitting}
          disabled={isSubmitting || !documentName}
        >
          Save
        </Button>
        <Button floated="right" onClick={handleClose}>
          Cancel
        </Button>
      </Segment>
    </ModalWrapper>
  );
}

